import { reactive, computed, onMounted, ref, watch } from 'vue'
import { ICertificationDetailState, IResponseCertificationDetail, ICertificationDetail, ICertificationItem } from '@/interface/certification'
import { ICourseListItem } from '@/interface/course'
import { useStoreHooks } from '@/store/hook'
import { useI18n } from 'vue-i18n'
import { useRouter, useRoute } from 'vue-router'
import { getDetail, getAllCourse, unenrollPracticalExam, getPracticalExamByCertificate, getPracticalRegionList, entroll } from '@/request/api/certification'
import { enrollPracticalExam, EMAIL_NOTIFICATION_STATUS } from '@/request/api/user'
import { IBannerListItem, UserClassroomItem, RegionList, EnrollSalesInvitationDetail } from '@/interface'
import { useClientCompatibleHook, useModalConfirmHook } from '@/utils/hooks'
import { CERTIFICATION_TYPE, IN_ENROLL_DIALOG_TYPE, ENROLL_RELATION_TYPE } from '@/utils/config'
import { getAtModel } from '@/utils/atModel'
import Message from '@/components/common/message'
import { enrollSalesInvitation } from '@/request/api/invitation'

interface IStepInfo {
  title: string
  content: string
}

export const COURSE_TAB_STATUS = {
  existence: '1',
  nonExistent: '0'
}

export const PLATFORM_TYPES = {
  skillLearn: '0',
  traditional: '1'
}

export const CERTIFICATION_STATUS = {
  entroll: '0',
  learning: '1',
  exam: '2', // 考试
  practicalExam: '3', // 实操考试，如果没配置，直接从2 -> 4
  apply: '4', // 待申请
  finish: '5', // 已申请，未审批
  approved: '6' // 已审批
}

export const CERTIFICATION_CHILD_STATUS = {
  beforeSignup: '0',
  reject: '1',
  pendding: '2',
  approve: '3'
}

export const checkIfExamAvailable = (status: string) => {
  const legalStatus = [CERTIFICATION_STATUS.exam, CERTIFICATION_STATUS.apply, CERTIFICATION_STATUS.finish]
  const show = legalStatus.some(s => status === s)
  return show
}

export function enableEnrollButton (status: string, childStatus: string) {
  if (status === CERTIFICATION_STATUS.entroll) {
    return childStatus === CERTIFICATION_CHILD_STATUS.beforeSignup || childStatus === CERTIFICATION_CHILD_STATUS.reject
  }
  return false
}

export function getCertificationStatusSubTitle (status: string, childStatus: string): string {
  // 这里返回的都是i18n的key
  if (status === CERTIFICATION_STATUS.entroll && childStatus === CERTIFICATION_CHILD_STATUS.reject) {
    return 'certificate.registerFailedTip'
  } else if (status === CERTIFICATION_STATUS.entroll && childStatus === CERTIFICATION_CHILD_STATUS.pendding) {
    return 'certificate.registerApprovedTip'
  } else if (status === CERTIFICATION_STATUS.exam || status === CERTIFICATION_STATUS.practicalExam) {
    return 'certificate.examTip'
  } else if (status === CERTIFICATION_STATUS.apply) {
    return 'certificate.readyToApplyTip'
  } else if (status === CERTIFICATION_STATUS.finish) {
    return 'certificate.appliedTip'
  } else if (status === CERTIFICATION_STATUS.approved) {
    return 'certificate.appliedSuccessTip'
  }
  return ''
}

export function changeStepStatus (state: ICertificationDetailState, status: string, childStatus: string) {
  const titleKey = getCertificationStatusSubTitle(status, childStatus)
  if (titleKey) {
    state.subTitle = titleKey
  } else {
    state.subTitle = ''
  }
}

export function createCertificationDetailReactive (): ICertificationDetailState {
  return reactive({
    certificationDetail: {} as ICertificationDetail,
    courseList: [] as ICourseListItem[],
    compulsoryList: [] as ICourseListItem[],
    electiveList: [] as ICourseListItem[],
    isLogin: false,
    kind: '',
    banner: {} as IBannerListItem,
    videoInfo: undefined,
    subTitle: '',
    visible: false,
    visibleEnroll: false,
    tabActiveKey: '1',
    examTabActiveKey: '1',
    currentStep: -1,
    disabled: true
  } as ICertificationDetailState)
}

export function setCertificationDetailReactive (state: ICertificationDetailState, data: IResponseCertificationDetail) {
  const { getters } = useStoreHooks()
  state.certificationDetail = data.certificationDetail || {}
  state.courseList = data.courseList
  state.compulsoryList = data.courseList.filter((item: ICourseListItem) => item.requirement === '1')
  state.electiveList = data.courseList.filter((item: ICourseListItem) => item.requirement === '0')
  state.isLogin = getters['global/isLogin']
  state.kind = data.certificationDetail.kind
  state.banner = data.banner || {}
  state.videoInfo = { url: data.certificationDetail.detailFile }
  state.disabled = data.certificationDetail.childStatus === CERTIFICATION_CHILD_STATUS.approve || data.certificationDetail.currentStatus === CERTIFICATION_STATUS.finish
  state.currentStep = parseInt(data.certificationDetail.currentStatus)
  // state.currentStep = parseInt(CERTIFICATION_STATUS.exam)
  changeStepStatus(state, data.certificationDetail.currentStatus, data.certificationDetail.childStatus)
}

export async function entrollToUpdate (state: ICertificationDetailState, id: string) {
  const result = await getDetail(id)
  const detail = result as IResponseCertificationDetail
  if (result) {
    state.certificationDetail = detail.certificationDetail
    state.courseList = detail.courseList
    state.currentStep = parseInt(detail.certificationDetail.currentStatus)
    changeStepStatus(state, detail.certificationDetail.currentStatus, detail.certificationDetail.childStatus)
  }
}
// 更新页面数据
export async function updateCertificationDetail (state: ICertificationDetailState, id: string) {
  const result = await getDetail(id)
  const detail = result as IResponseCertificationDetail
  if (result) {
    state.certificationDetail = detail.certificationDetail
    state.courseList = detail.courseList
    state.currentStep = parseInt(detail.certificationDetail.currentStatus)
    state.disabled = detail.certificationDetail.childStatus === CERTIFICATION_CHILD_STATUS.approve || detail.certificationDetail.currentStatus === CERTIFICATION_STATUS.finish
    console.log('ON_updateCertificationDetail', state.disabled, detail.certificationDetail)
    changeStepStatus(state, detail.certificationDetail.currentStatus, detail.certificationDetail.childStatus)
  }
}

export const useCertificationDetailHook = () => {
  const store = useStoreHooks()
  const { state: _state } = store
  const loaded = computed(() => _state.certification.loaded)
  const loading = ref(false)
  const { t } = useI18n()
  const route = useRoute()
  const state = createCertificationDetailReactive()
  const inEnrollDialogType = computed(() => (state.kind === CERTIFICATION_TYPE.hsc
    ? IN_ENROLL_DIALOG_TYPE.enrollWithInvitationCode : IN_ENROLL_DIALOG_TYPE.default))
  const inviteError = ref('')

  const onEnroll = async (mailSubscriptionStatus: boolean) => {
    const uncheckedStatus = _state.global.userInfo?.emailNotificationStatus === EMAIL_NOTIFICATION_STATUS.subscribed
      ? EMAIL_NOTIFICATION_STATUS.cancelSubscribe : EMAIL_NOTIFICATION_STATUS.notSubscribe
    const emailNotificationStatus = mailSubscriptionStatus ? EMAIL_NOTIFICATION_STATUS.subscribed : uncheckedStatus
    if (loading.value) return
    loading.value = true
    const certificationId = (route.query.id || route.params.id) as string
    const result = await entroll(certificationId, emailNotificationStatus as string)
    if (result.code === 200) {
      store.commit('global/setUserInfo', {
        ..._state.global.userInfo,
        emailNotificationStatus
      })
      const checkboxVisible = _state.global.userInfo?.emailNotificationStatus !== EMAIL_NOTIFICATION_STATUS.subscribed
      if (checkboxVisible && mailSubscriptionStatus) {
        Message.success(t('classroom.enrollAndMailSubscription'))
      } else {
        Message.success(t('classroom.enrollSuccess'))
      }
      state.visibleEnroll = false
      updateCertificationDetail(state, certificationId)
    }
    setTimeout(() => {
      loading.value = false
    }, 1000)
  }
  const confirmEnrollByInvite = async (data: EnrollSalesInvitationDetail) => {
    if (!data.code) {
      inviteError.value = t('classroom.inputInvitatioinCode')
      return
    }
    if (loading.value) return
    loading.value = true
    const certificationId = (route.query.id || route.params.id) as string
    data.relationId = certificationId
    data.relationType = ENROLL_RELATION_TYPE.certification
    const emailNotificationStatus = data.emailNotificationStatus
    const result = await enrollSalesInvitation(data)
    if (result?.code === 200) {
      Message.success(t('invitation.success'))
      state.visibleEnroll = false
      updateCertificationDetail(state, certificationId)
      store.commit('global/setUserInfo', {
        ..._state.global.userInfo,
        emailNotificationStatus
      })
    } else if (result && result.code === 10018) {
      const errorContext = result.errorContext || {}
      // 特殊动态文案
      inviteError.value = t('error.10018', { site: errorContext.codeValidWithSite || '' })
    } else if (result && result.code === 10019) {
      const errorContext = result.errorContext || {}
      // 特殊动态文案
      inviteError.value = t('error.10019', { country: errorContext.codeValidWithCountry || '' })
    } else if (result?.code) {
      inviteError.value = t(`error.${result?.code}`)
    }
    setTimeout(() => {
      loading.value = false
    }, 1000)
  }

  return {
    loaded,
    onEnroll,
    state,
    inEnrollDialogType,
    confirmEnrollByInvite,
    inviteError
  }
}

export function getSteps (): IStepInfo[] {
  const { t } = useI18n()
  return [
    {
      title: t('certificate.stepEnroll'),
      content: t('certificate.stepEnroll')
    },
    {
      title: t('certificate.setpLearn'),
      content: t('certificate.setpLearn')
    },
    {
      title: t('certificate.stepExam'),
      content: t('certificate.stepExam')
    },
    {
      title: t('certificate.apply'),
      content: t('certificate.apply')
    }
  ]
}

export function useIntroduction () {
  const { t } = useI18n()
  return {
    gscList: {
      title: t('gscCertificate.title'),
      desc: t('gscCertificate.desc'),
      width: 278,
      marginTop: 32,
      trainList: [
        { color: 'rgba(223,228,226,0.5)', img: require('@/assets/gsc-one.png'), desc: t('gscCertificate.traningOne') },
        { color: 'rgba(223,224,228,0.5)', img: require('@/assets/gsc-two.png'), desc: t('gscCertificate.traningTwo') },
        { color: 'rgba(237,233,230,0.5)', img: require('@/assets/gsc-three.png'), desc: t('gscCertificate.traningThree') }
      ]
    },
    hscList: {
      title: t('hscCertificate.title'),
      desc: t('hscCertificate.desc'),
      width: 372,
      marginTop: 27,
      trainList: [
        { color: 'rgba(223,228,226,0.5)', img: require('@/assets/hsc-one.png'), desc: t('hscCertificate.traningOne') },
        { color: 'rgba(223,224,228,0.5)', img: require('@/assets/hsc-two.png'), desc: t('hscCertificate.traningTwo') },
        { color: 'rgba(237,233,230,0.5)', img: require('@/assets/hsc-three.png'), desc: t('hscCertificate.traningThree') }
      ]
    }
  }
}

export function useCertificateI18nPrefix (kind: string) {
  return {
    i18nPrefix: computed(() => kind === CERTIFICATION_TYPE.gsc ? 'gscCertificate' : 'hscCertificate')
  }
}

// 获取页面tab信息
export function useTabsHook (detailData: IResponseCertificationDetail) {
  const { t } = useI18n()
  const tabs = [
    { value: '1', name: t('certificate.onlineVideoCourse') },
    { value: '2', name: t('certificate.onlineClassroomTrain'), isOnlineClass: true },
    { value: '3', name: t('certificate.onsiteClassroomTrain'), isOnsiteClass: true }
  ]
  const examTabs = computed(() => {
    const tabs = [{ value: '1', name: t('Theory Exam') }]
    if (detailData.certificationDetail.practicalExam === '1') {
      tabs.push({ value: '2', name: t('Practical Exam') })
    }
    return tabs
  })

  const showTabList = computed(() => {
    const showData = []
    if (detailData.courseList.length > 0) {
      showData.push(tabs[0])
    }
    if (detailData.certificationDetail.hasOnlineClassroom !== COURSE_TAB_STATUS.nonExistent) {
      showData.push(tabs[1])
    }
    if (detailData.certificationDetail.hasOnsiteClassroom !== COURSE_TAB_STATUS.nonExistent) {
      showData.push(tabs[2])
    }
    return showData
  })
  const defaultTabKey = showTabList.value[0]?.value || tabs[0].value
  return {
    examTabs,
    showTabList,
    tabs,
    defaultTabKey
  }
}

// 获取certification list && course list信息
export function useCurriculumHook () {
  const router = useRouter()
  const { state: certificationState } = useStoreHooks()
  const { isMobile } = useClientCompatibleHook()
  const typeList = computed(() => {
    const list = certificationState.certification.certificationList
    const result = [] as Array<{
      typeName: string
      list: ICertificationItem[]
    }>
    // 要根据typeName聚合
    list.forEach(obj => {
      for (const key in obj) {
        result.push({
          typeName: key,
          list: obj[key]
        })
      }
    })
    return result
  })
  const currentTypeItem = ref(typeList.value[0]?.list[0] || [])
  const compulsoryList = ref([] as ICourseListItem[])
  const electiveList = ref([] as ICourseListItem[])

  const loadCourse = async (item: ICertificationItem) => {
    const res = await getAllCourse(item.id)
    const items: Array<Partial<ICourseListItem>> = res.data as ICourseListItem[]
    compulsoryList.value = items.filter(item => item.requirement === '1') as ICourseListItem[]
    electiveList.value = items.filter(item => item.requirement === '0') as ICourseListItem[]
  }

  const onCertificationName = (item: ICertificationItem) => {
    currentTypeItem.value = item
    loadCourse(item)
  }

  const onToCourseDetail = (id: number) => {
    if (!id) return
    // 只用在认证overview页面跳转，courseType = 0
    router.push(`${isMobile.value ? '/mobile' : ''}/course/detail/${id}?courseType=0`)
  }

  const onToCertificationDetail = (id: string) => {
    router.push(`${isMobile.value ? '/mobile' : ''}/certification/detail/${id}#course-list-position`)
  }

  const toLearn = (id: string) => {
    router.push(`${isMobile.value ? '/mobile' : ''}/certification/detail/${id}`)
  }

  watch(() => typeList.value, () => {
    onCertificationName(typeList.value[0]?.list[0])
  })

  onMounted(() => {
    if (typeList.value.length) {
      loadCourse(typeList.value[0].list[0])
    }
  })

  return {
    typeList,
    currentTypeItem,
    compulsoryList,
    electiveList,
    compulsoryCredits: computed(() => compulsoryList.value
      .map?.(i => i.credits)
      .reduce?.((acc, cur) => acc + cur, 0)
    ),
    electiveCredits: computed(() => electiveList.value
      .map?.(i => i.credits)
      .reduce?.((acc, cur) => acc + cur, 0)
    ),
    ifNoCourseData: computed(() => {
      return electiveList.value.every(i => !i.id) && compulsoryList.value.every(i => !i.id)
    }),
    onCertificationName,
    onToCourseDetail,
    onToCertificationDetail,
    toLearn
  }
}

// 获取实操考试相关操作方法和状态
export function usePracticalExamHook (params?: { certificationId: number | string, certificationName: string }) {
  const { certificationId = '', certificationName = '' } = params || {}
  const state = reactive({
    regionId: null as null | string,
    loading: false,
    regionList: [] as Array<Partial<RegionList>>,
    dataList: [] as UserClassroomItem[],
    crtInfo: {} as UserClassroomItem
  })
  const { confirm } = useModalConfirmHook()
  const { t } = useI18n()

  // 初始化列表数据
  const getData = async () => {
    const res = await getPracticalExamByCertificate({
      certificationId,
      regionId: state.regionId || ''
    })
    const data = res.data || []
    state.dataList = data as UserClassroomItem[]
  }

  // 初始化地区数据
  const initRegionData = async () => {
    state.loading = true
    const region = await getPracticalRegionList({
      certificationId
    })
    const regionList = (region.data || []) as RegionList[]
    state.regionList = [{ name: t('global.filter.allCountry'), regionId: null }, ...regionList]
    await getData()
    state.loading = false
  }

  // 修改地区
  const onChangeRegion = (value: null | string) => {
    state.regionId = value
    getData()
  }

  // 取消报名
  const unenroll = (row: { id: number }, type = 'cancel') => {
    const tip = type === 'cancel' ? t('practicalExam.cancelTip')
      : t('practicalExam.unenrollTip')
    const resTip = type === 'cancel' ? t('classroom.cancelSuccess') : t('classroom.unenrollSuccess')
    confirm({
      content: tip,
      onOk () {
        state.loading = true
        unenrollPracticalExam({
          practicalExamId: row.id
        }).then(res => {
          if (res.code === 200) {
            setTimeout(() => {
              Message.success(resTip)
            }, 500)
          }
          state.loading = false
          getData()
        })
      }
    })
  }

  // 报名
  const enroll = (row: {
    id: number
    practicalExamName: string
    dietaryRequirements: string
    allergies: string
    e: Event
  }) => {
    getAtModel().doActionEvent(`${certificationName}_exam::practical_exam::${row.practicalExamName}_enroll`, row.e)
    state.loading = true
    enrollPracticalExam({
      practicalExamId: row.id,
      allergies: row.allergies,
      dietaryRequirements: row.dietaryRequirements
    }).then(res => {
      if (res.code === 200) {
        setTimeout(() => {
          Message.success(t('classroom.enrollSuccess'))
        }, 500)
      }
      state.loading = false
      getData()
    })
  }
  return {
    unenroll,
    enroll,
    initRegionData,
    state,
    onChangeRegion
  }
}
